import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ExportType, ExportMode, ExportRequestDto } from '../types/export';
import { apiService } from '../services/apiService';
import AuthService, { Partner } from '../services/authService';
import { useExportTypes } from '../hooks/useExportTypes';
import NumberedSection from '../components/selection/NumberedSection';
import FulfillmentContextSelection, { ALL_FCS, FcSelectionValue } from '../components/export/FulfillmentContextSelection';
import PartnerPicker from '../components/export/PartnerPicker';
import ExportTypeGrid from '../components/export/ExportTypeGrid';
import RelatedDataOptions from '../components/export/RelatedDataOptions';
import OutputSummary from '../components/export/OutputSummary';
import ExportActionBar from '../components/export/ExportActionBar';
import { IncludeFlag, itemNoun, relatedOptionsFor } from '../components/export/exportTypeMeta';

type ExportOptions = Omit<ExportRequestDto, 'username' | 'password' | 'partnerId' | 'exportType'>;

const INITIAL_OPTIONS: ExportOptions = {
  exportMode: ExportMode.CONSOLIDATED,
  includeRateCards: true,
  includeThirdPartyBilling: true,
  includeInjectionProfiles: true,
  includeLanes: true,
  includeLabelAugmentations: true,
  includeOrigins: true,
  includeCarrierSchedules: true,
  includeIncompleteFcs: true,
};

// Prefer the backend's validation message (e.g. unknown FC IDs) over axios's generic "status code 400".
const getErrorMessage = (error: any): string => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join('; ');
  if (typeof message === 'string') return message;
  return error instanceof Error ? error.message : 'Failed to create export job';
};

const plural = (n: number, noun: string) => `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;

const ExportPage: React.FC = () => {
  const navigate = useNavigate();
  const authService = AuthService.getInstance();
  const { types, loading: typesLoading, error: typesError, retry: retryTypes } = useExportTypes();
  const [partners] = useState<Partner[]>(() => authService.getPartners());
  const [selectedPartnerId, setSelectedPartnerId] = useState(() => authService.getSelectedPartner()?.partnerId ?? '');
  const [chosenType, setChosenType] = useState<ExportType | ''>('');
  const [options, setOptions] = useState<ExportOptions>(INITIAL_OPTIONS);
  const [fcSelection, setFcSelection] = useState<FcSelectionValue>(ALL_FCS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default to Fulfillment Contexts once the types have loaded.
  const exportType: ExportType | '' = chosenType || (types.length ? ExportType.FULFILLMENT_CONTEXTS : '');
  const typeLabel = types.find((t) => t.value === exportType)?.label ?? 'Records';
  const noun = itemNoun(exportType);
  const partner = partners.find((p) => p.partnerId === selectedPartnerId);

  const isFcExport = exportType === ExportType.FULFILLMENT_CONTEXTS;
  const missingFcSelection = isFcExport && !fcSelection.exportAllFCs && fcSelection.fulfillmentContextIds.length === 0;
  const relatedOptions = useMemo(() => relatedOptionsFor(exportType, options.exportMode), [exportType, options.exportMode]);
  const tabs = useMemo(() => [typeLabel, ...relatedOptions.filter((o) => options[o.key]).map((o) => o.label)], [typeLabel, relatedOptions, options]);

  const changePartner = useCallback((next: Partner) => {
    authService.setSelectedPartner(next);
    setSelectedPartnerId(next.partnerId);
    // A selection belongs to one partner.
    setFcSelection(ALL_FCS);
  }, [authService]);

  const changeType = useCallback((next: ExportType) => {
    setChosenType(next);
    setError(null);
  }, []);

  const changeMode = useCallback((exportMode: ExportMode) => setOptions((prev) => ({ ...prev, exportMode })), []);
  const isOptionOn = (key: IncludeFlag) => !!options[key];
  const toggleOption = useCallback((key: IncludeFlag) => setOptions((prev) => ({ ...prev, [key]: !prev[key] })), []);
  const setAllOptions = useCallback(
    (keys: IncludeFlag[], value: boolean) =>
      setOptions((prev) => keys.reduce<ExportOptions>((next, key) => ({ ...next, [key]: value }), prev)),
    [],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (!exportType) {
      setError('Please select an export type');
      return;
    }
    if (missingFcSelection) {
      setError('Select at least one fulfillment context, or choose All fulfillment contexts');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Use authenticated user's credentials and selected partner
      const authState = authService.getAuthState();
      const exportRequest: ExportRequestDto = {
        ...options,
        exportType,
        username: authState.username || '',
        password: authState.password || '',
        partnerId: authState.selectedPartner?.partnerId || '',
        ...(isFcExport &&
          (fcSelection.exportAllFCs
            ? { exportAllFCs: true }
            : { exportAllFCs: false, fulfillmentContextIds: fcSelection.fulfillmentContextIds })),
      };

      await apiService.createExportJob(exportRequest);

      // Redirect to Dashboard so user can see the job (and success state)
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
      setSubmitting(false);
    }
  };

  const hint = !partner
    ? 'Select a partner to export'
    : !exportType
      ? 'Select what to export'
      : missingFcSelection
        ? 'Select at least one fulfillment context to export'
        : isFcExport && !fcSelection.exportAllFCs
          ? `Exporting ${plural(fcSelection.fulfillmentContextIds.length, noun)} for ${partner.partnerName}`
          : `Exporting all ${typeLabel.toLowerCase()} for ${partner.partnerName}`;

  const relatedStep = isFcExport ? 4 : 3;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1040, width: '100%' }}>
      <Box>
        <Typography component="h1" sx={{ fontSize: 32, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
          Export partner data
        </Typography>
        <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 14 }}>
          Choose a partner and what to export. The job runs in the background and the files appear on your dashboard.
        </Typography>
      </Box>

      <Box component="form" noValidate onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <NumberedSection step={1} title="Partner" description="The partner whose configuration you want to export.">
          <PartnerPicker partners={partners} selectedId={selectedPartnerId} onChange={changePartner} />
        </NumberedSection>

        <NumberedSection step={2} title="What to export" description="The type of configuration data to export. Each export job covers one type.">
          <ExportTypeGrid types={types} value={exportType} loading={typesLoading} error={typesError} onChange={changeType} onRetry={retryTypes} />
        </NumberedSection>

        {isFcExport && <FulfillmentContextSelection step={3} partnerId={selectedPartnerId} value={fcSelection} onChange={setFcSelection} />}

        {relatedOptions.length > 0 && (
          <RelatedDataOptions step={relatedStep} typeLabel={typeLabel} options={relatedOptions} isOn={isOptionOn} onToggle={toggleOption} onSetAll={setAllOptions} />
        )}

        {exportType && <OutputSummary mode={options.exportMode} onModeChange={changeMode} noun={noun} tabs={tabs} />}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <ExportActionBar hint={hint} submitting={submitting} disabled={!partner || !exportType || missingFcSelection} />
      </Box>
    </Box>
  );
};

export default ExportPage;
